/* VerifyEmail — landing page the user hits after clicking the link in
   their email. Status comes via `?status=ok|expired` query param that
   the backend's `/api/auth/verify/{token}` endpoint redirects to. */
import { useSearchParams, Link } from "react-router-dom";
import { Check, AlertCircle, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const status = params.get("status") || "ok";
  const { user } = useAuth();
  const [resending, setResending] = useState(false);

  const resend = async () => {
    if (!user) {
      toast.error("Please sign in first.");
      return;
    }
    setResending(true);
    try {
      await api.post("/auth/request-verification");
      toast.success(`Sent a fresh link to ${user.email}. Check your inbox.`);
    } catch {
      toast.error("Couldn't send a new link — please try again.");
    } finally {
      setResending(false);
    }
  };

  const ok = status === "ok";

  return (
    <div className="min-h-screen grid place-items-center px-6 py-12" data-testid={`verify-page-${status}`}>
      <div className="max-w-[440px] w-full text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-10">
          <div className="w-9 h-9 rounded-xl bg-foreground text-accent grid place-items-center font-bold">v</div>
          <span className="font-semibold tracking-tight">verqo</span>
        </Link>
        <div className={`w-14 h-14 mx-auto rounded-full grid place-items-center mb-5 border ${ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
          {ok ? <Check className="w-6 h-6 text-emerald-700"/> : <AlertCircle className="w-6 h-6 text-rose-700"/>}
        </div>
        {ok ? (
          <>
            <h1 className="display text-3xl tracking-tight">Email confirmed</h1>
            <p className="text-foreground/65 mt-3 text-[14px] leading-relaxed">
              Thanks — your email is verified. You'll now receive order updates, invoices, and security alerts.
            </p>
            <Link
              to={user ? `/${user.role}` : "/login"}
              data-testid="verify-continue"
              className="mt-7 inline-flex items-center justify-center h-11 px-6 rounded-full bg-foreground text-background hover:bg-foreground/90"
            >
              Continue to {user ? "dashboard" : "sign in"}
            </Link>
          </>
        ) : (
          <>
            <h1 className="display text-3xl tracking-tight">Link expired</h1>
            <p className="text-foreground/65 mt-3 text-[14px] leading-relaxed">
              This verification link is no longer valid. Verification links expire after 24 hours for security.
            </p>
            <div className="mt-7 flex flex-col gap-2 items-center">
              {user ? (
                <button
                  onClick={resend}
                  disabled={resending}
                  data-testid="verify-resend"
                  className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60"
                >
                  {resending ? <RefreshCw className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
                  {resending ? "Sending…" : "Send a new link"}
                </button>
              ) : (
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center h-11 px-6 rounded-full bg-foreground text-background hover:bg-foreground/90"
                >
                  Sign in to request a new link
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
